import React from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Monitor } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { PageHero, SectionIntro, ClosingCta, CheckList, faqSchema, usePrimaryCta } from '../components/common/B2B';
import { Section, ItemsSection, FaqSection, HeroChecks, PhoneShot } from '../components/common/B2BSections';
import ClientLogos from '../components/common/ClientLogos.jsx';

// Transportistas y flotas (nov-2026), contenido compuesto desde cero (Jesús:
// «siéntete libre de componer nuevos contenidos»): el mismo documento visto desde
// la cabina y desde tráfico, lo que necesita una flota (subcontratación, trasvases,
// archivo), integración, documentos y FAQ. Sin el bloque de obligaciones del DeCA
// ni el 5 de octubre: la responsabilidad va en una pregunta, con la redacción ya
// revisada en la página del DeCA. «Sin cobertura»: consultar y mostrar con la app.
// El formulario propio se sustituye por la reserva común con
// ?origin=transportistas&profile=carrier.
// Hero: conductor junto a su camión, puertas cerradas (la primera foto abría la
// puerta al revés); GPT Image 2, marketing/prompts-imagenes/hero-transportistas-conductor.txt.
// Captura real del visor del conductor (private-app) con una carta de porte ficticia
// y la API interceptada, sin datos de clientes: marketing/capturas-producto/.
// Versión inglesa (COPY.en, /en/carriers) añadida el 4-nov-2026; la captura sigue siendo la de la app en español.

const PAGE_URL = { es: 'https://www.bluecmr.com/transportistas', en: 'https://www.bluecmr.com/en/carriers' };
const QUERY = '?origin=transportistas&profile=carrier';

const COPY = {
  es: {
    seoTitle: 'Documentos de transporte digitales para transportistas | BlueCMR',
    seoDescription: 'El conductor recibe, muestra y firma el DeCA, la carta de porte o el eCMR en el móvil sin instalar nada, y tráfico ve cada entrega firmada al momento.',
    seoKeywords: 'documentos de transporte transportista, deca transportista, carta de porte digital, ecmr transportista, firma en el móvil del conductor, prueba de entrega digital',
    heroEyebrow: 'Para transportistas y flotas',
    heroTitle: 'El documento viaja en el móvil del conductor',
    heroText: 'Para empresas de transporte y flotas: tráfico emite o recibe los documentos de cada servicio, el conductor los muestra y recoge las firmas desde el móvil, sin instalar nada, y cada entrega firmada llega a la oficina en cuanto se produce.',
    heroChecks: ['Sin instalar nada', 'Listo para una inspección', 'Entregas firmadas al momento'],
    twoEyebrow: 'Cabina y oficina',
    twoTitle: 'Un mismo documento, dos puestos de trabajo',
    screen: {
      src: '/images/soluciones/visor-conductor-movil.webp',
      alt: 'Carta de porte abierta en el móvil del conductor, con los botones QR, Firmar y Más',
      caption: 'Lo que ve el conductor.',
    },
    two: [
      {
        icon: Smartphone,
        title: 'En la cabina',
        text: 'El conductor solo necesita su móvil.',
        items: [
          'Recibe el documento por WhatsApp, SMS o enlace',
          'Lo enseña en una inspección; con la app, también sin cobertura',
          'Recoge en su pantalla la firma del cargador y la del destinatario',
          'Anota incidencias y reservas con fotografías',
          'Registra el intercambio de palets al firmar la entrega',
        ],
      },
      {
        icon: Monitor,
        title: 'En tráfico',
        text: 'La oficina sigue cada servicio sin tener que llamar al conductor.',
        items: [
          'Emite desde su TMS, importando cualquier fichero o con plantillas por cliente y ruta',
          'O recibe el documento que ya ha creado el cargador, sin teclearlo otra vez',
          'Cambia matrícula, vehículo o conductor sobre la marcha',
          'Recibe un aviso con cada firma o incidencia',
          'Factura con la prueba de entrega ya firmada',
        ],
      },
    ],
    needs: {
      eyebrow: 'Pensado para flotas',
      title: 'Lo que necesita una empresa de transporte',
      items: [
        { title: 'Subcontratación', text: 'Si cede un servicio, el documento se transfiere al nuevo transportista con todo su historial.' },
        { title: 'Trasvases', text: 'El paso de la carga a otro vehículo o a un centro logístico queda registrado en el documento.' },
        { title: 'Un archivo para todo', text: 'Lo que emite usted, lo que le comparten sus cargadores con BlueCMR y los PDF que le llegan de otros sistemas, juntos y con búsqueda.' },
        { title: 'Varias bases', text: 'Cada delegación con sus usuarios y documentos, y una visión conjunta para la central.' },
        { title: 'Rutas de reparto', text: 'Un solo DeCA para varias entregas o recogidas que comparten cargador y transportista.' },
        { title: 'Internacional y ADR', text: 'eCMR para los viajes internacionales y documento ADR con el DeCA para la mercancía peligrosa.' },
      ],
    },
    faqEyebrow: 'Preguntas frecuentes',
    faqTitle: 'Lo que suelen preguntarnos los transportistas',
    faq: [
      { q: '¿Los conductores tienen que instalar algo?', a: 'No. Reciben el documento por WhatsApp, SMS o enlace y lo abren en el navegador del móvil. La app de BlueCMR es opcional: añade el histórico de documentos y la consulta sin cobertura.' },
      { q: '¿Qué me toca como transportista?', a: 'En el transporte nacional, el transportista efectivo formaliza el DeCA y lo lleva a bordo, y responde junto al cargador contractual de que exista y esté bien cumplimentado. No llevarlo, o llevarlo en papel o incompleto, se sanciona conforme al régimen sancionador de la LOTT.' },
      { q: '¿Y si el documento lo hace el cargador?', a: 'Lo recibe ya hecho: el conductor lo muestra y recoge las firmas y, si usted tiene cuenta en BlueCMR, queda en su histórico sin teclearlo otra vez.' },
      { q: '¿Qué pasa si subcontrato el servicio?', a: 'Quien subcontrata pasa a ser el cargador contractual del nuevo envío. En BlueCMR, el documento se transfiere al nuevo transportista con todo su historial.' },
      { q: '¿Se integra con mi TMS?', a: 'Sí, por API REST y webhooks, o sin programar, con los ficheros que ya genera su sistema, en cualquier formato e incluso en PDF, por FTP o por correo. Para grandes volúmenes coordinamos un piloto con integración.' },
    ],
    closing: 'Véalo con sus propios servicios',
    closingText: 'Una demostración con su operativa real, sin compromiso.',
    breadcrumb: ['Inicio', 'Transportistas'],
  },
  en: {
    seoTitle: 'Digital transport documents for carriers and fleets | BlueCMR',
    seoDescription: 'Drivers receive, show and sign the DeCA, consignment note or eCMR on their phone with nothing to install, and the office sees every signed delivery at once.',
    seoKeywords: 'transport documents for carriers, deca control document, digital consignment note, ecmr for carriers, signature on the driver\'s phone, digital proof of delivery',
    heroEyebrow: 'For carriers and fleets',
    heroTitle: 'The document travels on the driver\'s phone',
    heroText: 'For transport companies and fleets: the traffic office issues or receives the documents for each job, the driver shows them and collects the signatures on the phone, with nothing to install, and every signed delivery reaches the office as soon as it happens.',
    heroChecks: ['Nothing to install', 'Ready for roadside checks', 'Signed deliveries straight away'],
    twoEyebrow: 'Cab and office',
    twoTitle: 'One document, two places of work',
    screen: {
      src: '/images/soluciones/visor-conductor-movil-en.webp',
      alt: 'Consignment note open on the driver\'s phone, with the QR, Sign and More buttons',
      caption: 'What the driver sees.',
    },
    two: [
      {
        icon: Smartphone,
        title: 'In the cab',
        text: 'The driver only needs a phone.',
        items: [
          'Receives the document by WhatsApp, SMS or link',
          'Shows it at a roadside check; with the app, even without coverage',
          'Collects the shipper\'s and the consignee\'s signatures on screen',
          'Records incidents and reservations with photographs',
          'Records the pallet exchange when the delivery is signed',
        ],
      },
      {
        icon: Monitor,
        title: 'In the traffic office',
        text: 'Planners follow every job without having to call the driver.',
        items: [
          'Issue from your TMS, by importing any file or with templates per customer and route',
          'Or receive the document the shipper has already created, without retyping it',
          'Change plate, vehicle or driver on the fly',
          'Get a notification for every signature or incident',
          'Invoice with the proof of delivery already signed',
        ],
      },
    ],
    needs: {
      eyebrow: 'Designed for fleets',
      title: 'What a transport company needs',
      items: [
        { title: 'Subcontracting', text: 'If you subcontract a job, the document is transferred to the new carrier with its full history.' },
        { title: 'Transhipments', text: 'Moving the load to another vehicle or to a logistics centre is recorded in the document.' },
        { title: 'One archive for everything', text: 'What you issue, what your shippers share with you through BlueCMR and the PDFs that reach you from other systems, together and searchable.' },
        { title: 'Several depots', text: 'Each branch with its own users and documents, and an overall view for head office.' },
        { title: 'Multi-drop routes', text: 'A single control document (DeCA) for several deliveries or collections that share shipper and carrier.' },
        { title: 'International and ADR', text: 'eCMR for international trips, and the ADR document together with the control document for dangerous goods.' },
      ],
    },
    faqEyebrow: 'FAQ',
    faqTitle: 'What carriers usually ask us',
    faq: [
      { q: 'Do drivers have to install anything?', a: 'No. They receive the document by WhatsApp, SMS or link and open it in the phone\'s browser. The BlueCMR app is optional: it adds the document history and access without coverage.' },
      { q: 'What falls to me as a carrier?', a: 'In domestic transport in Spain, the actual carrier completes the control document (DeCA) and keeps it on board, and is responsible, together with the contractual shipper, for its existence and for it being filled in correctly. Not carrying it, or carrying it on paper or incomplete, is penalised under the sanctions regime of the Spanish road transport act (LOTT).' },
      { q: 'What if the shipper creates the document?', a: 'You receive it ready-made: the driver shows it and collects the signatures and, if you have a BlueCMR account, it stays in your history without retyping it.' },
      { q: 'What happens if I subcontract the job?', a: 'For the control document in Spain, whoever subcontracts becomes the contractual shipper of the new shipment. In BlueCMR, the document is transferred to the new carrier with its full history.' },
      { q: 'Does it integrate with my TMS?', a: 'Yes, via the REST API and webhooks or, with no coding, using the files your system already produces, in any format, PDF included, by FTP or email. For large volumes, we coordinate a pilot including the integration.' },
    ],
    closing: 'See it with your own transport jobs',
    closingText: 'A demonstration with your real operations, with no commitment.',
    breadcrumb: ['Home', 'Carriers'],
  },
};

const TransportistasPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const primaryCta = usePrimaryCta(lang);

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="website"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[1], url: PAGE_URL[lang] },
        ]}
        structuredData={[faqSchema(c.faq)]}
      />

      <div className="bg-white">
        <PageHero
          eyebrow={c.heroEyebrow}
          title={c.heroTitle}
          text={c.heroText}
          image={{ src: '/images/soluciones/hero-transportistas-conductor.webp', position: '50% 50%' }}
          primary={{ ...primaryCta, to: `${primaryCta.to}${QUERY}` }}
          compact
        >
          <HeroChecks items={c.heroChecks} />
        </PageHero>

        {/* Logos de clientes: siempre en B/N y pequeños (condición de su permiso) */}
        <ClientLogos title={lang === 'es' ? 'Algunas de las empresas que trabajan con BlueCMR' : 'Some of the companies working with BlueCMR'} />

        {/* Cabina y oficina */}
        <Section tone="white">
          <SectionIntro eyebrow={c.twoEyebrow} title={c.twoTitle} />
          <div className="grid md:grid-cols-[minmax(0,15rem)_1fr_1fr] gap-x-14 gap-y-14">
            <PhoneShot {...c.screen} />
            {c.two.map((col) => {
              const Icon = col.icon;
              return (
                <div key={col.title} className="border-t-2 border-slate-900 pt-6">
                  <Icon className="w-7 h-7 text-primary-700 mb-5" strokeWidth={1.5} />
                  <h3 className="text-2xl font-semibold tracking-tight text-slate-900">{col.title}</h3>
                  <p className="mt-3 text-slate-700 leading-relaxed">{col.text}</p>
                  <CheckList items={col.items} className="mt-6 space-y-2.5" />
                </div>
              );
            })}
          </div>
        </Section>

        <ItemsSection {...c.needs} tone="gray" />
        <FaqSection eyebrow={c.faqEyebrow} title={c.faqTitle} items={c.faq} tone="white" />

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} query={QUERY} />
      </div>
    </>
  );
};

export default TransportistasPage;
